import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import styles from './MultiSelect.module.css';

export interface MultiSelectOption {
	value: string;
	label: string;
	disabled?: boolean;
}

export interface MultiSelectProps {
	values: string[];
	onChange: (values: string[]) => void;
	options: MultiSelectOption[];
	placeholder?: string;
	hasError?: boolean;
	disabled?: boolean;
	showSelectAll?: boolean;
}

export const MultiSelect = ({
	values,
	onChange,
	options,
	placeholder = 'Selecione',
	hasError,
	disabled,
	showSelectAll = false,
}: MultiSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
	const wrapperRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		setDropdownStyle({
			top: rect.bottom + 4,
			left: rect.left,
			width: rect.width,
		});
	}, []);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(target) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(target)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			updatePosition();
			document.addEventListener('mousedown', handleOutsideClick);
			document.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);
		}

		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
			document.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isOpen, updatePosition]);

	const toggleOption = (value: string) => {
		if (options.find((option) => option.value === value)?.disabled) return;
		if (values.includes(value)) {
			onChange(values.filter((v) => v !== value));
		} else {
			onChange([...values, value]);
		}
	};

	const enabledValues = options.filter((option) => !option.disabled).map((option) => option.value);
	const allSelected =
		enabledValues.length > 0 && enabledValues.every((value) => values.includes(value));

	const toggleAll = () => {
		if (allSelected) {
			onChange(values.filter((value) => !enabledValues.includes(value)));
			return;
		}

		onChange(Array.from(new Set([...values, ...enabledValues])));
	};

	const displayText =
		values.length === 0
			? ''
			: values.length === 1
				? (options.find((o) => o.value === values[0])?.label ?? values[0])
				: `${values.length} selecionados`;

	const dropdown = isOpen ? (
		<div ref={dropdownRef} className={styles.dropdown} style={dropdownStyle}>
			{showSelectAll && enabledValues.length > 0 && (
				<div className={`${styles.option} ${styles.selectAll}`} onMouseDown={toggleAll}>
					<div className={`${styles.checkbox} ${allSelected ? styles.checkboxChecked : ''}`}>
						{allSelected && <CheckOutlined className={styles.checkmark} />}
					</div>
					{allSelected ? 'Desmarcar todos' : 'Marcar todos'}
				</div>
			)}
			<div className={styles.optionsList}>
				{options.length === 0 ? (
					<div className={styles.noResults}>Nenhuma opção disponível</div>
				) : (
					options.map((option) => {
						const selected = values.includes(option.value);
						return (
							<div
								key={option.value}
								className={`${styles.option} ${option.disabled ? styles.optionDisabled : ''}`}
								onMouseDown={() => toggleOption(option.value)}
								aria-disabled={option.disabled}
							>
								<div className={`${styles.checkbox} ${selected ? styles.checkboxChecked : ''}`}>
									{selected && <CheckOutlined className={styles.checkmark} />}
								</div>
								{option.label}
							</div>
						);
					})
				)}
			</div>
		</div>
	) : null;

	return (
		<div ref={wrapperRef} className={styles.wrapper}>
			<button
				ref={triggerRef}
				type="button"
				disabled={disabled}
				onClick={() => !disabled && setIsOpen((o) => !o)}
				className={`${styles.trigger} ${values.length === 0 ? styles.empty : ''} ${hasError ? styles.error : ''} ${isOpen ? styles.open : ''}`}
			>
				<span className={`${styles.triggerText} ${!displayText ? styles.placeholder : ''}`}>
					{displayText || placeholder}
				</span>
				<ExpandMoreOutlined className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
			</button>
			{typeof document !== 'undefined' && createPortal(dropdown, document.body)}
		</div>
	);
};
